import {
  LegacyModal,
  ModalHeader,
  ModalContent,
  ModalFooter,
  ButtonGroup,
  BasicButton,
  PrimaryButton,
  ModalTrigger,
  Text,
  GridItem,
  RadioGroupField,
  Grid,
  TextArea,
  Radio,
} from "@certn/cascadia-react";
import { components } from "@certn/legacy-api/generated-schemas/people";
import { Field, Form, useField } from "formik";
import { t } from "i18next";
import { Trans } from "react-i18next";

import { useSkipReason, getReferencesRuleResults } from "./utils";

type EmployerReferencePreSaveModalProps = {
  field: components["schemas"]["EmploymentReferencesCheckField"];
  trigger: JSX.Element;
  handleNext: () => void;
};

export function EmployerReferencePreSaveModal({
  field,
  trigger,
  handleNext,
}: EmployerReferencePreSaveModalProps) {
  const [values, , helpers] = useField(field.name);
  const currentRefCount = values.value?.references?.length;
  const hasNoReferences = currentRefCount === 0;
  const { requireAmount, requiredCount, requireYears } =
    getReferencesRuleResults({ field });

  const handleOnChange = (value: string) => {
    const newEmploymentReferences = {
      ...values.value,
      employer_reference_skip_reason: value,
    };
    helpers.setValue(newEmploymentReferences);
  };

  const skipReason = values.value?.employer_reference_skip_reason;
  const { skipReasons } = useSkipReason();

  const hasOtherSkipReason = ![...Object.keys(skipReasons), null].includes(
    skipReason
  );

  return (
    <ModalTrigger trigger={() => trigger}>
      {(close) => (
        <LegacyModal width="456px">
          <ModalHeader
            close={close}
            title={t("Employment references incomplete")}
            testID="employment-references-incomplete-modal"
          />
          <ModalContent>
            <Form>
              <Grid form>
                <GridItem>
                  {requireYears ? (
                    <YearsTitle requiredCount={requiredCount} />
                  ) : (
                    <AmountTitle
                      currentCount={currentRefCount}
                      requiredCount={requiredCount}
                    />
                  )}
                </GridItem>
                <GridItem>
                  <Text style="global/body-primary">
                    <Trans>
                      If you would like to proceed anyway, please select a
                      reason.
                    </Trans>
                  </Text>
                </GridItem>
                <GridItem>
                  <Field
                    isRequired
                    direction="column"
                    as={RadioGroupField}
                    onChange={handleOnChange}
                    name="employment_references.employer_reference_skip_reason"
                    aria-label="employer_reference_skip_reason radio button selections"
                    testID="employment-references-incomplete-modal-skip-section-radio-group"
                  >
                    {requireAmount &&
                      (hasNoReferences ? (
                        <Radio
                          value="NO_EMPLOYER_REFERENCES"
                          testID="employment-references-incomplete-modal-no-employer-references-radio"
                        >
                          {skipReasons["NO_EMPLOYER_REFERENCES"]}
                        </Radio>
                      ) : (
                        <Radio
                          value="NOT_ENOUGH_EMPLOYER_REFERENCES"
                          testID="employment-references-incomplete-modal-not-enough-references-radio"
                        >
                          {skipReasons["NOT_ENOUGH_EMPLOYER_REFERENCES"]}
                        </Radio>
                      ))}
                    {requireYears && (
                      <Radio
                        value="NO_RECENT_EMPLOYER_REFERENCES"
                        testID="employment-references-incomplete-modal-no-recent-employer-reference-radio"
                      >
                        {skipReasons["NO_RECENT_EMPLOYER_REFERENCES"]}
                      </Radio>
                    )}
                    <Radio
                      value="DO_NOT_CONTACT_EMPLOYER_REFERENCES"
                      testID="employment-references-incomplete-modal-do-not-contact-references-radio"
                    >
                      {skipReasons["DO_NOT_CONTACT_EMPLOYER_REFERENCES"]}
                    </Radio>
                    <Radio value={hasOtherSkipReason ? skipReason : ""}>
                      <TextArea
                        isRequired
                        value={hasOtherSkipReason ? skipReason : ""}
                        onChange={handleOnChange}
                        name="other"
                        label={t("Other")}
                        maxLength={100}
                        defaultValue={
                          hasOtherSkipReason ? skipReason : undefined
                        }
                        testID="employment-references-incomplete-modal-other-input"
                      />
                    </Radio>
                  </Field>
                </GridItem>
              </Grid>
            </Form>
          </ModalContent>

          <ModalFooter>
            <ButtonGroup>
              <BasicButton
                size="medium"
                onPress={close}
                testID="employment-references-incomplete-modal-cancel-button"
              >
                <Trans>Cancel</Trans>
              </BasicButton>
              <PrimaryButton
                type="submit"
                size="medium"
                onPress={handleNext}
                testID="employment-references-incomplete-modal-continue-button"
              >
                <Trans>Continue</Trans>
              </PrimaryButton>
            </ButtonGroup>
          </ModalFooter>
        </LegacyModal>
      )}
    </ModalTrigger>
  );
}

const YearsTitle = ({ requiredCount }: { requiredCount: number }) => (
  <Text style="global/body-primary">
    <Trans>
      Please provide at least one employer reference within the last{" "}
      <strong>{{ requiredCount } as unknown as JSX.Element}</strong> years.
      Meeting the requirements will produce quicker results.
    </Trans>
  </Text>
);

const AmountTitle = ({
  requiredCount,
  currentCount,
}: {
  requiredCount: number;
  currentCount: number;
}) => (
  <Text style="global/body-primary">
    <Trans>
      You've added{" "}
      <strong>
        {{ currentCount } as unknown as JSX.Element} of{" "}
        {{ requiredCount } as unknown as JSX.Element}
      </strong>{" "}
      employer references. Meeting the requirements will produce quicker
      results.
    </Trans>
  </Text>
);
